// Shared in-browser-LLM engine state for the Playground offline-mode flow.
// Driven by the OfflineModeSwitch (and the one-time offline offer) and consumed
// by every practice page that supports offline mode (The Instrument, Body
// Language). Promoting the engine + download-progress state to a shared store
// means:
//
//   1. Toggle ON ⇒ download starts immediately (no need to click Start).
//      This is the architecture redirect the user asked for: the download
//      is toggle-driven, not Start-driven. The previous `runOfflineStart`
//      approach folded download + generate into the Start click, which
//      surprised the user (they expected the toggle to commit to the
//      download as the heavy first step).
//
//   2. Both practice pages reflect the same engine state. A user toggling
//      offline on The Instrument and navigating to Body Language sees the
//      already-downloaded engine; they don't pay the 700 MB tax twice.
//
//   3. Model-picker switches can immediately unload + redownload without
//      any per-page coordination — the store is the single owner.
//
// Public API surface:
//   `offlineEngineStore`       — readable<OfflineEngineState>
//   `selectedOfflineModelId`   — readable<string> (persisted to localStorage)
//   `enableOfflineEngine()`    — call when the user toggles ON
//   `disableOfflineEngine()`   — call when the user toggles OFF
//   `selectOfflineModel(id)`   — call when the user picks a different model;
//                                immediately unloads + redownloads if already
//                                loaded, otherwise just updates the
//                                persisted preference for the next enable()
//   `offlineModelCatalog`      — the offered models, once a load fetched them

import { get, writable, type Readable } from 'svelte/store';
import { loadModelCatalog, type CatalogModel } from '$lib/modelMirror/modelMirror';
import {
  DEFAULT_MODEL_ID,
  detectWebGpu,
  loadModel,
  pickModel,
  resolveModelId,
  type InBrowserEngine,
  type ModelLoadPhase,
  type ModelLoadProgress,
} from '$lib/utils/ai/inBrowserLlm';

/**
 * The engine-store state machine. Pages branch on `status`:
 *   - `'idle'`     — toggle is OFF (or never been turned on). No engine.
 *   - `'loading'`  — downloading, then loading into memory. `progress` 0..1,
 *                    `text` is the MLC pipeline's human-readable status,
 *                    `phase` says whether files are still coming from the
 *                    network (`'download'`, also while that is not yet known)
 *                    or the model is loading from the browser cache onto the
 *                    GPU (`'load'`), `modelId` is the model being loaded.
 *   - `'ready'`    — engine is loaded and usable for generation. `modelId`
 *                    is the loaded model; pages call generatePracticeContent
 *                    against `engine`.
 *   - `'error'`    — load failed. `error` is the human-readable message;
 *                    the page can show it and let the user re-toggle or pick
 *                    a different model.
 */
export type OfflineEngineState =
  | { status: 'idle' }
  | { status: 'loading'; progress: number; text: string; phase: ModelLoadPhase; modelId: string }
  | { status: 'ready'; engine: InBrowserEngine; modelId: string }
  | { status: 'error'; error: string; modelId: string };

/** localStorage key for the selected model id. Single global preference
 *  (not per-practice — the model is a per-user/device choice). */
const MODEL_ID_STORAGE_KEY = 'vd.offline.modelId';

/** localStorage key for whether the user has toggled offline mode on.
 *  Global, single boolean — shared across all practice pages so toggling
 *  off on one page tears the engine down for the other. Enable/disable is
 *  global, store-driven; there are no per-practice offline-mode keys. */
const ENABLED_STORAGE_KEY = 'vd.offline.enabled';

// ---------------------------------------------------------------------------
// Internal mutable state
// ---------------------------------------------------------------------------

// The store. Initialised to 'idle'; the OfflineModeSwitch's onMount hydrates
// from localStorage and calls enableOfflineEngine() if the persisted flag is
// on. Keeping the initial state as 'idle' (rather than reading localStorage
// at module-eval time) is SSR-safe.
const internalStore = writable<OfflineEngineState>({ status: 'idle' });

/** Public readable view. */
export const offlineEngineStore: Readable<OfflineEngineState> = {
  subscribe: internalStore.subscribe,
};

// Selected model id, persisted to localStorage. Initialised to the default;
// hydrated lazily on first call (SSR-safe — we never touch localStorage at
// module-eval time).
const selectedModelStore = writable<string>(DEFAULT_MODEL_ID);
let hydrated = false;

/** Public readable view of the selected model id. */
export const selectedOfflineModelId: Readable<string> = {
  subscribe: selectedModelStore.subscribe,
};

const catalogStore = writable<readonly CatalogModel[] | null>(null);

export const offlineModelCatalog: Readable<readonly CatalogModel[] | null> = {
  subscribe: catalogStore.subscribe,
};

// In-flight load identifier — a monotonic counter so a late-arriving progress
// callback from a SUPERSEDED load (e.g., user switched model mid-download)
// doesn't overwrite the newer load's state. Each enable()/selectModel() call
// bumps this; the in-flight load checks `loadGeneration === my generation`
// before each emit.
let loadGeneration = 0;

// Cached `shader-f16` capability, resolved once per session via detectWebGpu().
// f16 models (`q4f16_1` / `q0f16`) require the WebGPU `shader-f16` extension
// (Chrome: D3D12/Windows + Metal/macOS, NOT Vulkan/Linux); without it we must
// load the f32 sibling. We resolve precision at LOAD time (not at select time)
// so the persisted stable id stays valid across an OS/browser switch.
//
// `null` = not yet detected. `startLoad` awaits detection on first use and
// memoises the boolean so subsequent model switches don't re-probe the GPU.
let shaderF16Cache: boolean | null = null;

/**
 * Resolves (and memoises) whether the adapter supports `shader-f16`. The probe
 * is cheap (a `requestAdapter()` + `features.has`) but we only need it once;
 * any failure resolves conservatively to `false` (f32 path), which loads on
 * every working adapter.
 */
async function getShaderF16(): Promise<boolean> {
  if (shaderF16Cache !== null) return shaderF16Cache;
  try {
    const detection = await detectWebGpu();
    shaderF16Cache = detection.shaderF16;
  } catch {
    // Detection itself threw — fall back to the universally-loadable f32 path.
    shaderF16Cache = false;
  }
  return shaderF16Cache;
}

// ---------------------------------------------------------------------------
// Hydration
// ---------------------------------------------------------------------------

/**
 * Reads the persisted model id (and enabled flag) from localStorage and
 * applies them to the stores. Idempotent — calling it more than once is a
 * no-op after the first run. Browser-only; the caller must guard SSR.
 *
 * Returns `true` when the persisted enabled flag was ON (so the caller knows
 * to trigger `enableOfflineEngine()` after mount). Returns `false` when OFF
 * or absent. We DON'T auto-call enable here — the OfflineModeSwitch is the
 * authority over the toggle UI state and must drive both the visual switch
 * and the engine in the same flow.
 */
export function hydrateOfflineStoreFromStorage(): boolean {
  if (hydrated) {
    return get(internalStore).status !== 'idle';
  }
  hydrated = true;

  try {
    // Checked against the catalog when a load fetches it.
    const storedId = window.localStorage.getItem(MODEL_ID_STORAGE_KEY);
    if (storedId) selectedModelStore.set(storedId);
  } catch {
    // localStorage blocked — fall back to defaults; the toggle still works
    // in memory, just doesn't survive a refresh.
  }

  try {
    const storedEnabled = window.localStorage.getItem(ENABLED_STORAGE_KEY);
    return storedEnabled === '1';
  } catch {
    return false;
  }
}

function persistModelId(id: string): void {
  try {
    window.localStorage.setItem(MODEL_ID_STORAGE_KEY, id);
  } catch {
    // localStorage blocked — keep the in-memory selection.
  }
}

function persistEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(ENABLED_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // localStorage blocked.
  }
}

// ---------------------------------------------------------------------------
// Public actions
// ---------------------------------------------------------------------------

/**
 * Toggles offline mode ON and kicks off the download for the currently
 * selected model. Idempotent — if a load is already in flight for the same
 * model, returns without starting a second one. If the engine is already
 * ready for the selected model, returns immediately.
 *
 * Persists the enabled flag to localStorage so a refresh re-triggers the
 * download for the previously-selected model.
 */
export function enableOfflineEngine(): void {
  persistEnabled(true);
  const modelId = get(selectedModelStore);
  const current = get(internalStore);

  if (current.status === 'ready' && current.modelId === modelId) {
    // Already loaded — no-op. The toggle was probably already on; nothing
    // for us to do.
    return;
  }
  if (current.status === 'loading' && current.modelId === modelId) {
    // Already loading this exact model. Don't start a duplicate.
    return;
  }

  startLoad(modelId);
}

/**
 * Toggles offline mode OFF. Drops the engine reference and resets state to
 * `'idle'`. Any in-flight load is "abandoned" — we can't actually cancel
 * web-llm's network fetches mid-stream (the SDK doesn't expose an abort
 * API), but bumping `loadGeneration` ensures progress callbacks from the
 * doomed load are ignored and the resulting engine (if it does finish
 * loading) is dropped on the floor.
 *
 * The web-llm in-tab engine cache inside `inBrowserLlm.ts` is also cleared
 * so a subsequent enable() does a fresh load — this matters when the user
 * toggles off and switches model before toggling back on.
 */
export function disableOfflineEngine(): void {
  persistEnabled(false);
  loadGeneration++; // invalidate any in-flight load callbacks
  internalStore.set({ status: 'idle' });
}

/**
 * Updates the selected model id and persists it. If the engine is already
 * loaded OR currently loading, immediately unloads + restarts the download
 * for the new model (the user explicitly asked for "unload current,
 * download new immediately" — no prompt, no defer).
 *
 * If offline mode is OFF (idle), just records the preference — the new model
 * will be loaded on the next enable().
 */
export function selectOfflineModel(modelId: string): void {
  if (!get(catalogStore)?.some((m) => m.id === modelId)) return;
  const previousId = get(selectedModelStore);
  if (previousId === modelId) return;

  selectedModelStore.set(modelId);
  persistModelId(modelId);

  const current = get(internalStore);
  // Only trigger a reload if offline mode is currently engaged. If the user
  // is just exploring the picker with offline mode OFF, we record the
  // preference and stop there.
  if (current.status === 'idle') return;

  // Active state — immediate unload + redownload.
  startLoad(modelId);
}

// ---------------------------------------------------------------------------
// Internal load orchestrator
// ---------------------------------------------------------------------------

/**
 * Starts a fresh load for the stable `modelId`. Bumps `loadGeneration` so any
 * prior in-flight load's callbacks become no-ops, sets the store to
 * `'loading'` with progress 0, fetches the catalog (an id it no longer
 * offers is replaced by the default, persisted), resolves the precision
 * variant (f16 vs f32, per the adapter's `shader-f16` support) and awaits
 * `loadModel(resolvedId, ...)` in a fire-and-forget async block. On success:
 * `'ready'`; on failure: `'error'`.
 *
 * NOTE the store state always carries the STABLE `modelId` (the f16 base key
 * the picker binds + localStorage persists). Only the id passed to
 * `loadModel` is precision-resolved — so the picker's `value={modelId}`
 * matching and the persisted preference stay platform-independent.
 */
function startLoad(requestedId: string): void {
  let modelId = requestedId;
  loadGeneration++;
  const myGeneration = loadGeneration;

  internalStore.set({
    status: 'loading',
    progress: 0,
    text: 'Preparing the in-browser model…',
    phase: 'download',
    modelId,
  });

  void (async () => {
    try {
      const { models } = await loadModelCatalog();
      if (myGeneration !== loadGeneration) return;
      catalogStore.set(models);
      const model = pickModel(models, modelId);
      if (model.id !== modelId) {
        modelId = model.id;
        selectedModelStore.set(modelId);
        persistModelId(modelId);
        internalStore.update((s) => (s.status === 'loading' ? { ...s, modelId } : s));
      }
      // On a no-`shader-f16` adapter (e.g. Linux Chrome) load the f32 sibling.
      const shaderF16 = await getShaderF16();
      if (myGeneration !== loadGeneration) return;
      const engine = await loadModel(resolveModelId(model, shaderF16), (p: ModelLoadProgress) => {
        // Drop callbacks from superseded loads (model-switch in flight).
        if (myGeneration !== loadGeneration) return;
        internalStore.set({
          status: 'loading',
          progress: Math.max(0, Math.min(1, p.progress)),
          text: p.text,
          phase: p.phase,
          modelId,
        });
      });
      if (myGeneration !== loadGeneration) return;
      internalStore.set({ status: 'ready', engine, modelId });
    } catch (err) {
      if (myGeneration !== loadGeneration) return;
      const message = err instanceof Error ? err.message : 'Unknown error.';
      internalStore.set({
        status: 'error',
        error: message,
        modelId,
      });
    }
  })();
}

// ---------------------------------------------------------------------------
// Test hooks
// ---------------------------------------------------------------------------

/**
 * Test-only reset — restores the store to its initial state and invalidates
 * any in-flight load. Not exported via the public surface; vitest reaches it
 * via the module import.
 */
export function __resetOfflineEngineStoreForTests(): void {
  loadGeneration++;
  hydrated = false;
  shaderF16Cache = null;
  selectedModelStore.set(DEFAULT_MODEL_ID);
  catalogStore.set(null);
  internalStore.set({ status: 'idle' });
}
